/**
 * Inline form for registering a new git server. Extracted from
 * GitServerManager to keep that component under the 200-line cap.
 */

import { useState, type ReactElement } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gitApi } from '../../git/api/gitApi';
import { useAdminLoginProviders } from '../../logins/hooks/useAdminLoginProviders';
import type { GitServerCreateRequest, ProviderType } from '../../git/types/git.types';

const PROVIDER_TYPES: ProviderType[] = ['gitlab', 'github', 'bitbucket', 'gitea', 'generic'];

function extractErrorDetail(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === 'string') return detail;
  }
  return null;
}

export function GitServerRegistrationForm(): ReactElement {
  const qc = useQueryClient();
  const { data: providers } = useAdminLoginProviders();
  const [displayName, setDisplayName] = useState('');
  const [providerType, setProviderType] = useState<ProviderType>('gitlab');
  const [baseUrl, setBaseUrl] = useState('');
  const [serviceToken, setServiceToken] = useState('');
  const [loginProviderId, setLoginProviderId] = useState('');

  // A git server only accepts tokens it issued itself, so the main Sanctum
  // login can never be a git credential here even when the git server signs
  // in against that same IdP — offering it produces a server wired to a
  // token its host will reject on every call. The backend rejects the
  // pairing too; this keeps it off the menu in the first place.
  const attachableProviders = (providers ?? []).filter(
    (p) => p.enabled && p.type !== 'main_session',
  );

  const create = useMutation({
    mutationFn: (body: GitServerCreateRequest) => gitApi.createServer(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gitServers'] });
      setDisplayName('');
      setBaseUrl('');
      setServiceToken('');
      setLoginProviderId('');
    },
  });

  const submit = (): void => {
    create.mutate({
      display_name: displayName,
      provider_type: providerType,
      base_url: baseUrl,
      ...(serviceToken ? { service_account_token: serviceToken } : {}),
      login_provider_id: loginProviderId || null,
    });
  };

  return (
    <section className="git-server-manager__form-section">
      <h2>Register New Server</h2>
      <div className="git-server-manager__form">
        <div className="git-server-manager__form-field">
          <label htmlFor="displayName">Display Name</label>
          <input
            id="displayName"
            placeholder="e.g., ACME GitLab"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
            }}
            className="git-server-manager__input"
          />
        </div>

        <div className="git-server-manager__form-field">
          <label htmlFor="providerType">Provider Type</label>
          <select
            id="providerType"
            value={providerType}
            onChange={(e) => {
              setProviderType(e.target.value as ProviderType);
            }}
            className="git-server-manager__select"
          >
            {PROVIDER_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="git-server-manager__form-field">
          <label htmlFor="baseUrl">Base URL</label>
          <input
            id="baseUrl"
            placeholder="https://gitlab.acme.com"
            value={baseUrl}
            onChange={(e) => {
              setBaseUrl(e.target.value);
            }}
            className="git-server-manager__input"
          />
        </div>

        <div className="git-server-manager__form-field">
          <label htmlFor="serviceToken">Service Account Token (optional)</label>
          <input
            id="serviceToken"
            type="password"
            placeholder="Used for admin browse operations"
            value={serviceToken}
            onChange={(e) => {
              setServiceToken(e.target.value);
            }}
            className="git-server-manager__input"
          />
        </div>

        <div className="git-server-manager__form-field">
          <label htmlFor="loginProviderId">Login Provider (optional)</label>
          <select
            id="loginProviderId"
            value={loginProviderId}
            onChange={(e) => {
              setLoginProviderId(e.target.value);
            }}
            className="git-server-manager__select"
          >
            <option value="">
              Auto-detect — use the OAuth app registered on this server
            </option>
            {attachableProviders.map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name}
              </option>
            ))}
          </select>
          <small className="help-text">
            Users connect with their own account instead of pasting a token.
            Left on auto-detect, the login whose issuer is this server is
            attached automatically; if there is none, users fall back to
            personal access tokens. Only an OAuth application registered on
            the git server itself can issue credentials it will accept.
          </small>
        </div>

        <div className="git-server-manager__form-actions">
          <button
            onClick={submit}
            disabled={create.isPending || !displayName || !baseUrl}
            className="admin-page__create-btn"
          >
            {create.isPending ? 'Registering…' : 'Register Server'}
          </button>
          {create.isError && (
            <p className="git-server-manager__error" role="alert">
              {extractErrorDetail(create.error) ??
                'Failed to register server. Please try again.'}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
